import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import {
  Type,
  FileText,
  Image,
  Video,
  Clock,
  Tag,
  Globe,
  ListOrdered,
  DollarSign,
  CheckCircle,
  XCircle,
} from 'lucide-react';

interface CourseFormProps {
  form: any;
  setForm: (updater: (f: any) => any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  saving: boolean;
  categories: Array<{ id: number; name: string }>;
  trainers: Array<{ id: number; name: string }>;
  isEdit: boolean;
}

const tabs = [
  { id: 'basic', label: 'Basic', icon: Type },
  { id: 'content', label: 'Content', icon: FileText },
  { id: 'media', label: 'Media', icon: Image },
  { id: 'seo', label: 'SEO', icon: Globe },
];

export default function CourseForm({
  form,
  setForm,
  onSubmit,
  onCancel,
  saving,
  categories,
  trainers,
  isEdit,
}: CourseFormProps): React.JSX.Element {
  const [activeTab, setActiveTab] = useState('basic');

  const update = (key: string, value: any) => setForm((f: any) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!isEdit && form.title && !form.slug) {
      const generated = form.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      update('slug', generated);
    }
  }, [form.title, isEdit]);

  const addTag = (field: 'benefits' | 'skills_covered') => {
    const val = (window as any).prompt(`Enter ${field === 'benefits' ? 'benefit' : 'skill'}:`);
    if (val?.trim()) {
      update(field, [...form[field], val.trim()]);
    }
  };

  const removeTag = (field: 'benefits' | 'skills_covered', idx: number) => {
    update(field, form[field].filter((_: any, i: number) => i !== idx));
  };

  return (
    <form onSubmit={onSubmit} className="p-6 space-y-4">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-100 pb-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-amber-50 text-amber-600'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Basic Tab */}
      {activeTab === 'basic' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Title"
              required
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
            />
            <Input
              label="Slug"
              required
              value={form.slug}
              onChange={(e) => update('slug', e.target.value)}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Price"
              type="number"
              required
              icon={<DollarSign className="w-4 h-4 text-slate-400" />}
              value={form.price}
              onChange={(e) => update('price', e.target.value)}
            />
            <Input
              label="Discount Price"
              type="number"
              icon={<DollarSign className="w-4 h-4 text-slate-400" />}
              value={form.discount_price}
              onChange={(e) => update('discount_price', e.target.value)}
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-slate-700">Status</label>
              <select
                value={form.status}
                onChange={(e) => update('status', e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              >
                <option value="draft">Draft</option>
                <option value="pending_review">Pending Review</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-slate-700">Category</label>
              <select
                value={form.category_id}
                onChange={(e) => update('category_id', e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-slate-700">Trainer</label>
              <select
                value={form.trainer_id}
                onChange={(e) => update('trainer_id', e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              >
                <option value="">Select trainer</option>
                {trainers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Duration"
              placeholder="e.g., 8 weeks"
              icon={<Clock className="w-4 h-4 text-slate-400" />}
              value={form.duration}
              onChange={(e) => update('duration', e.target.value)}
            />
            <Input
              label="Sort Order"
              type="number"
              icon={<ListOrdered className="w-4 h-4 text-slate-400" />}
              value={form.sort_order}
              onChange={(e) => update('sort_order', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <Input
            label="Short Description"
            multiline
            required
            value={form.short_description}
            onChange={(e) => update('short_description', e.target.value)}
          />

          <Input
            label="Full Description"
            multiline
            value={form.full_description}
            onChange={(e) => update('full_description', e.target.value)}
          />

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Benefits</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.benefits.map((b: string, i: number) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium"
                >
                  <CheckCircle className="w-3 h-3" />
                  {b}
                  <button type="button" onClick={() => removeTag('benefits', i)} className="hover:text-green-900">
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <Button type="button" size="sm" variant="secondary" onClick={() => addTag('benefits')} leftIcon={<Tag className="w-3 h-3" />}>
              Add Benefit
            </Button>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Skills Covered</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.skills_covered.map((s: string, i: number) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-600 text-xs font-medium"
                >
                  <Tag className="w-3 h-3" />
                  {s}
                  <button type="button" onClick={() => removeTag('skills_covered', i)} className="hover:text-amber-900">
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <Button type="button" size="sm" variant="secondary" onClick={() => addTag('skills_covered')} leftIcon={<Tag className="w-3 h-3" />}>
              Add Skill
            </Button>
          </div>
        </div>
      )}

      {/* Media Tab */}
      {activeTab === 'media' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <Input
            label="Course Image URL"
            icon={<Image className="w-4 h-4 text-slate-400" />}
            value={form.course_image}
            onChange={(e) => update('course_image', e.target.value)}
          />
          {form.course_image && (
            <img src={form.course_image} alt="Preview" className="w-32 h-20 object-cover rounded-xl border border-slate-200" />
          )}

          <Input
            label="Banner Image URL"
            icon={<Image className="w-4 h-4 text-slate-400" />}
            value={form.banner_image}
            onChange={(e) => update('banner_image', e.target.value)}
          />
          {form.banner_image && (
            <img src={form.banner_image} alt="Preview" className="w-full h-24 object-cover rounded-xl border border-slate-200" />
          )}

          <Input
            label="Intro Video URL"
            icon={<Video className="w-4 h-4 text-slate-400" />}
            value={form.intro_video}
            onChange={(e) => update('intro_video', e.target.value)}
          />
        </div>
      )}

      {/* SEO Tab */}
      {activeTab === 'seo' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <Input
            label="SEO Title"
            value={form.seo_title}
            onChange={(e) => update('seo_title', e.target.value)}
          />
          <Input
            label="SEO Description"
            multiline
            value={form.seo_description}
            onChange={(e) => update('seo_description', e.target.value)}
          />
          <Input
            label="Meta Keywords"
            placeholder="keyword1, keyword2, keyword3"
            value={form.meta_keywords}
            onChange={(e) => update('meta_keywords', e.target.value)}
          />
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={saving}>
          {isEdit ? 'Update Course' : 'Create Course'}
        </Button>
      </div>
    </form>
  );
}
